import { useEffect } from "react";
import { Outlet, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useBrands, useBrand } from "@/hooks/use-brands";
import { BrandProvider } from "@/contexts/brand-context";
import { Skeleton } from "@/components/ui/skeleton";
import { BudgetExhaustedBanner } from "@/components/dashboard/budget-exhausted-banner";
import { WizardModal } from "@/components/wizard/wizard-modal";
import { AssistantPanel } from "@/components/assistant/assistant-panel";
import { writeLastBrandSlug } from "@/lib/last-brand";

/**
 * Resolves the brand from the :brandSlug URL param and provides BrandContext
 * to all nested routes. Renders the BudgetExhaustedBanner and WizardModal
 * once for the whole brand subtree (matches the old (app)/[brandSlug]/layout.tsx).
 */
export function BrandLayout() {
  const { t } = useTranslation("brand");
  const { brandSlug } = useParams<{ brandSlug: string }>();

  const { data: brands, isLoading: brandsLoading } = useBrands();
  const brandListItem = brands?.find((b) => b.slug === brandSlug);
  const brandId = brandListItem?.id ?? "";

  const { data: brand, isLoading: brandLoading, refetch: refetchBrand } = useBrand(brandId);

  // Remember this brand so opening the app root reopens it.
  const resolvedSlug = brand ? brandListItem?.slug : undefined;
  useEffect(() => {
    if (resolvedSlug) writeLastBrandSlug(resolvedSlug);
  }, [resolvedSlug]);

  if (brandsLoading || brandLoading) {
    return (
      <div className="space-y-4 p-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-4 w-full" />
      </div>
    );
  }

  if (!brand) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center space-y-3">
          <p className="text-muted-foreground">{t("notFound")}</p>
          <a href="/dashboard" className="text-sm underline underline-offset-4">
            {t("goToDashboard")}
          </a>
        </div>
      </div>
    );
  }

  const currentUserRole = brand.currentUserRole ?? brandListItem?.role ?? "VIEWER";

  return (
    <BrandProvider brand={brand} currentUserRole={currentUserRole} refetchBrand={() => { void refetchBrand(); }}>
      <BudgetExhaustedBanner />
      <Outlet />
      {brandId && <WizardModal brandId={brandId} />}
      {brandId && <AssistantPanel />}
    </BrandProvider>
  );
}
